// useNavHeader.js
import { useMemo } from 'react';
import { matchPath, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';

import { HELP_ARTICLE_ID_MESSAGE_PACK } from '../RemoteConfigKeys.js';

import useMessageDetailType from '../hook/useMessageDetailType.js';

import {
  UserMoreFeature,
  ProfileListType,
} from '../resource/profileConstants.js';
import { LIVESTREAM_PATH } from '../resource/liveStreamConstants.js';
import trackMixpanel, {
  EventTypes,
  ButtonId,
  getViewId,
} from '../resource/mixpanel.js';
import { breakpoint } from '../style/variables.js';

import toggleModal from '../action/toggleModal.js';

import getMessageData from '../selector/getMessageData.js';
import getUserData from '../selector/getUserData.js';
import getMeData from '../selector/getMeData.js';
import { MessageMoreFeature } from '../resource/messageConstants.js';
import getShareLink from '../resource/getShareLink.js';
import getCanShare from '../resource/getCanShare.js';
import { ProductType } from '../resource/shopConstants.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getProductData from '../selector/getProductData.js';

const canShare = getCanShare();

const trackUserClick = ({ buttonId, extraProps }) => {
  trackMixpanel({
    type: EventTypes.USER_CLICKED,
    payload: {
      viewId: getViewId({ pathname: window.location.pathname }),
      buttonId,
      extraProps,
    },
  });
};

export const defaultData = {
  titleSize: 22,
  usernameSize: 22,
  minWidth: 0,
  username: '',
  userId: null,
  title: '',
  shouldFixedTop: false,
  shouldShowUsernameOnMobile: false,
  shouldShowUserStatusOnMobile: false,
  shouldShowMoreOnMobile: false,
  shouldShowTitleOnMobile: false,
  shouldShowLogoOnMobile: false,
  shouldShowBackOnMobile: false,
  shouldShowLoginSignUpOnMobile: false,
  shouldShowDiamondShopOnMobile: false,
  shouldShowNotificationOnMobile: false,
  shouldShowUploadOnMobile: false,
  shouldShowLeaderboardOnMobile: false,
  shouldHidden: false,
  showOnlyOnDesktop: false,
  onClickMore: () => null,
};

export function useNavHeader() {
  const { userId = '', messageId = '', productId = '' } = useParams();
  const dispatch = useDispatch();
  const senderId = useSelector(state =>
    getMessageData(state, messageId, 'senderId')
  );
  const meId = useSelector(state => getMeData(state, 'id'));
  const isMeProfile = meId && meId === userId;

  const username = useSelector(state =>
    isMeProfile
      ? getMeData(state, 'username')
      : getUserData(state, userId, 'username')
  );
  const senderName = useSelector(state =>
    getUserData(state, senderId, 'username')
  );
  const productType = useSelector(state =>
    getProductData(state, [productId], 'type')
  );
  const articleConfigKey = productId
    ? ProductType.MESSAGEPACKS === productType
      ? HELP_ARTICLE_ID_MESSAGE_PACK
      : undefined
    : undefined;
  const articleId = useSelector(state =>
    getRemoteConfigData(state, articleConfigKey)
  );

  const { pathname } = useLocation();
  const layoutType = useMessageDetailType();

  return useMemo(() => {
    const userProfileDefaultData = {
      shouldFixedTop: true,
      shouldShowUsernameOnMobile: true,
      shouldShowLoginSignUpOnMobile: true,
      shouldShowMoreOnMobile: true,
      shouldShowLeaderboardOnMobile: false,
      username,
      usernameSize: isMeProfile ? 22 : 18,
      userId: isMeProfile ? meId : userId,
      shouldShowBackOnMobile: !isMeProfile,
      shouldShowUploadOnMobile: isMeProfile,
      shouldShowNotificationOnMobile: isMeProfile,
      shouldShowDiamondShopOnMobile: isMeProfile,
      shouldShowUserStatusOnMobile: !isMeProfile,
      onClickMore: () => {
        trackUserClick({
          buttonId: ButtonId.All.ButtonMore,
        });
        return dispatch(
          toggleModal({
            modalId: 'ProfileMoreModal',
            payload: {
              userId: isMeProfile ? meId : userId,
              features: [
                ...(!isMeProfile ? [UserMoreFeature.BLOCK] : []),
                canShare ? UserMoreFeature.SHARE : UserMoreFeature.COPY,
              ],
            },
            isOpen: true,
          })
        );
      },
    };

    const pathDataList = [
      {
        id: 'feed',
        pathname: '/feed',
        shouldFixedTop: true,
        title: 'feeds_title',
        shouldShowLoginSignUpOnMobile: true,
        shouldShowUploadOnMobile: true,
        shouldShowLeaderboardOnMobile: true,
        shouldShowNotificationOnMobile: true,
        shouldShowDiamondShopOnMobile: true,
        shouldShowTitleOnMobile: true,
      },
      {
        id: 'home',
        pathname: '/',
        shouldFixedTop: true,
        title: '',
        shouldShowLoginSignUpOnMobile: true,
        shouldShowUploadOnMobile: true,
        shouldShowLeaderboardOnMobile: true,
        shouldShowNotificationOnMobile: true,
        shouldShowDiamondShopOnMobile: true,
        shouldShowLogoOnMobile: true,
        shouldShowTitleOnMobile: false,
      },
      {
        id: 'home-video',
        pathname: '/video',
        shouldFixedTop: true,
        title: 'tabbar_discover',
        shouldShowLoginSignUpOnMobile: true,
        shouldShowUploadOnMobile: true,
        shouldShowLeaderboardOnMobile: true,
        shouldShowNotificationOnMobile: true,
        shouldShowDiamondShopOnMobile: true,
        shouldShowLogoOnMobile: false,
        shouldShowTitleOnMobile: true,
      },
      {
        id: 'user-profile',
        pathname: '/user/:userId',
        ...userProfileDefaultData,
      },
      {
        id: 'user-profile-image',
        pathname: `/user/:userId/${ProfileListType.SHORT}`,
        ...userProfileDefaultData,
      },
      {
        id: 'user-profile-image',
        pathname: `/user/:userId/${ProfileListType.IMAGE}`,
        ...userProfileDefaultData,
      },
      {
        id: 'user-profile-flix',
        pathname: `/user/:userId/${ProfileListType.FLIX}`,
        ...userProfileDefaultData,
      },
      {
        id: 'user-profile-livestream-session',
        pathname: `/user/:userId/${ProfileListType.LIVESTREAM_SESSION}`,
        ...userProfileDefaultData,
      },
      {
        id: 'user-profile-short',
        pathname: '/u/:username/*',
        ...userProfileDefaultData,
      },
      {
        id: 'livestreams',
        pathname: `/user/:userId/${LIVESTREAM_PATH}`,
        shouldFixedTop: true,
        title: 'home_feature',
        minWidth: breakpoint.tablet,
      },
      {
        id: 'chat',
        pathname: '/chat/*',
        shouldFixedTop: true,
        minWidth: breakpoint.mobile,
      },
      {
        id: 'flix-detail',
        pathname: '/post/:postId',
        shouldFixedTop: true,
        username: senderName,
        userId: senderId,
        usernameSize: 18,
        shouldShowBackOnMobile: true,
        shouldShowUsernameOnMobile: true,
        shouldShowUserStatusOnMobile: true,
        shouldShowLoginSignUpOnMobile: true,
        shouldShowMoreOnMobile: true,
        onClickMore: () => {
          trackUserClick({
            buttonId: ButtonId.All.ButtonMore,
          });
          return dispatch(
            toggleModal({
              modalId: 'MessageMoreModal',
              payload: {
                messageId,
                features: [
                  MessageMoreFeature.BLOCK,
                  MessageMoreFeature.FOLLOW,
                  MessageMoreFeature.PROFILE,
                  canShare ? MessageMoreFeature.SHARE : MessageMoreFeature.COPY,
                ],
                shareLink: getShareLink({
                  pathname: `/post/${messageId}`,
                }),
              },
              isOpen: true,
            })
          );
        },
      },
      {
        id: 'post-detail',
        pathname: '/post/:postId',
        shouldFixedTop: true,
        title: 'post_title',
        titleSize: 18,
        shouldShowLoginSignUpOnMobile: true,
        shouldShowBackOnMobile: true,
        shouldShowTitleOnMobile: true,
      },
      {
        id: 'short-detail',
        pathname: '/post/:postId',
        shouldFixedTop: true,
        minWidth: breakpoint.mobile,
      },
      {
        id: 'story-detail',
        pathname: '/story/:messageId',
        shouldFixedTop: true,
        minWidth: breakpoint.mobile,
      },
      {
        id: 'message-detail',
        pathname: '/message/:messageId',
        shouldFixedTop: true,
        minWidth: breakpoint.mobile,
      },
      {
        id: 'search',
        pathname: '/search/*',
        shouldFixedTop: true,
        minWidth: breakpoint.mobile,
      },
      {
        id: 'shorts',
        pathname: '/shorts/*',
        shouldFixedTop: true,
        minWidth: breakpoint.mobile,
      },
      {
        id: 'product-detail',
        pathname: '/product/:productId',
        shouldFixedTop: true,
        shouldShowBackOnMobile: true,
        shouldShowTitleOnMobile: true,
        shouldShowInfoOnMobile: true,
        title: 'message_packs',
        titleSize: 18,
        onClickInfo: () => {
          trackUserClick({
            buttonId: ButtonId.All.ButtonInformation,
            extraProps: {
              'article.config_key': articleConfigKey,
            },
          });
          return dispatch(
            toggleModal({
              modalId: 'IntercomArticleModal',
              payload: {
                articleId,
                articleConfigKey,
              },
              isOpen: true,
              modalOption: {
                transitionStatus: 'coming',
              },
            })
          );
        },
      },
    ];

    const matchedData =
      pathDataList.find(({ pathname: path, id }) => {
        if (matchPath(path, pathname)) {
          if (path === '/post/:postId') {
            if (layoutType === 'story' && id === 'short-detail') return true;
            if (layoutType === 'flix' && id === 'flix-detail') return true;
            if (layoutType === 'post' && id === 'post-detail') return true;

            return false;
          }

          return true;
        }
      }) || {};

    return {
      ...defaultData,
      ...(isEmpty(matchedData) ? { shouldHidden: true } : matchedData),
    };
  }, [
    articleId,
    articleConfigKey,
    messageId,
    pathname,
    isMeProfile,
    meId,
    dispatch,
    senderId,
    senderName,
    userId,
    username,
    layoutType,
  ]);
}

export default useNavHeader;
